import { ViewChild } from '@angular/core';
import { Output } from '@angular/core';
import { Input, OnChanges } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { clone } from '../common/functions/common-helpers';
import { CpqInputComponent } from '../cpq-input/cpq-input.component';
import { ListBoxItem } from '../cpq-list-box/ListBoxItem';
import { ILabel } from '../ILabel.model';

@Component({
    selector: 'cpq-list-box-with-selection',
    templateUrl: './cpq-list-box-with-selection.component.html',
    styleUrls: ['./cpq-list-box-with-selection.component.scss'],
    standalone: false
})
export class CpqListBoxWithSelectionComponent implements OnChanges {
  @Input('labelAvailable') labelAvailable: ILabel;
  @Input('availableInfoMessage') availableInfoMessage?: ILabel;
  @Input('labelSelected') labelSelected: ILabel;
  @Input('emptySelectedInfoMessage') emptySelectedInfoMessage: ILabel;
  @Input('available') available: Array<ListBoxItem>;
  @Input('selected') selected: Array<ListBoxItem>;
  @Output('onSelect') onSelect = new EventEmitter<ListBoxItem>();
  @Output('onRemove') onRemove = new EventEmitter<ListBoxItem>();
  @ViewChild('selectedGroupsInput', { static: true }) selectedGroupsInput: CpqInputComponent;
  @ViewChild('availableGroupsInput', { static: true }) availableGroupsInput: CpqInputComponent;

  public searchKeywordAvailable: string;
  public searchKeywordSelected: string;
  public filteredAvailableItems: Array<ListBoxItem>;
  public filteredSelectedItems: Array<ListBoxItem> = new Array<ListBoxItem>();
  public hasSelectedSearchResults = true;
  public showSearchIconAvailable = true;
  public hasAvailableSearchResults = true;
  public showSearchIconSelected = true;
  public selectedItemHeight = 33;
  public selectedItemsContainerMaxHeight = 396;
  
  constructor() { }

  ngOnChanges() {
    if (this.available && this.available.length > 0 && this.selected){
      this.initAvailableItems(true);
      this.setAllSelectedItemsVisible();

      if (this.searchKeywordAvailable) {
        this.searchAvailable();
      }
    }
  }

  private initAvailableItems(initFilteredItems: boolean) {
    this.available.forEach(item => {
      item.isVisible = this.selected.find(x => x.id === item.id) ? false : true;
    })
    if (initFilteredItems) {
      this.filteredAvailableItems = this.getFilteredItems(this.available);
    }
  }

  setAllSelectedItemsVisible() {
    this.selected.forEach(item => item.isVisible = true);
    this.filteredSelectedItems = this.getFilteredItems(this.selected);
  }

  private getFilteredItems(items: Array<ListBoxItem>): Array<ListBoxItem> {
    const filteredItems = new Array<ListBoxItem>();
    items.filter(x => x.isVisible).forEach(item => {
      const filteredItem = clone(item);
      filteredItems.push(filteredItem);
    });

    return filteredItems;
  }
  
  searchAvailable() {
    this.initAvailableItems(false);
    this.hasAvailableSearchResults = false;
    if (this.searchKeywordAvailable !== '') {
      this.available.filter(x => x.isVisible).forEach(availableItem => {
        if(availableItem.text.toLowerCase().indexOf(this.searchKeywordAvailable.toLowerCase()) > -1){
          this.hasAvailableSearchResults = true;
          availableItem.isVisible = true;
        }else{
          availableItem.isVisible = false;
        }
        this.showSearchIconAvailable = false;
      });
    } else {
      this.showSearchIconAvailable = true;
      this.hasAvailableSearchResults = true;
    }
    this.filteredAvailableItems = this.getFilteredItems(this.available);
  }

  searchSelected() {
    this.selected.forEach(item => { item.isVisible = true; });
    this.hasSelectedSearchResults = false;
    if (this.searchKeywordSelected !== '') {
      this.showSearchIconSelected = false;
      this.selected.forEach(item => {
        if ((item.text.toLowerCase()).indexOf(this.searchKeywordSelected.toLowerCase()) === -1) {
          item.isVisible = false;
        } else {
          this.hasSelectedSearchResults = true;
        }
      });
    } else {
      this.showSearchIconSelected = true;
      this.hasSelectedSearchResults = true;
    }
    this.filteredSelectedItems = this.getFilteredItems(this.selected);
  }

  removeSelectedInteractive(item: ListBoxItem, selectedItem: HTMLLIElement) {
    selectedItem.style.opacity = '0';
    setTimeout(() => {
      this.removeSelected(item);
      selectedItem.style.opacity = '1';
    }, 200);
  }

  removeSelected(item: ListBoxItem) {
    const index: number = this.selected.findIndex(x =>x.id === item.id);
    if (index > -1) {
      this.selected.splice(index, 1);
      this.addToAvailableItems(item);
      this.filteredAvailableItems = this.getFilteredItems(this.available);
      this.filteredSelectedItems = this.getFilteredItems(this.selected);
      this.onRemove.next(item);
    }
  }

  addToAvailableItems(item: ListBoxItem) {
    const availableItem = this.available.find(availableItem => availableItem.id === item.id);
    if (availableItem) {
      availableItem.isVisible = true;
    }
  }

  removeAllSelected() {
    this.selected.forEach(item => {
      this.addToAvailableItems(item);
      this.onRemove.next(item);
    });
    
    this.selected.length = 0;
    if (this.searchKeywordAvailable) {
      this.searchAvailable();
    } else {
      this.initAvailableItems(true);
    }

    if (this.searchKeywordSelected) {
      this.searchSelected();
    } else {
      this.filteredAvailableItems = this.getFilteredItems(this.available);
    }

    this.filteredSelectedItems = [];
  }
  
  addSelected(item: ListBoxItem, shouldUpdateFilteredItems: boolean = true) {
    if (!this.selected.find(x => x.id === item.id)) {
      const foundItem = this.available.find(availableItem => availableItem.id === item.id);
      if (foundItem) {
        foundItem.isVisible = false;
      }
      const selectedItem = clone(item);
      selectedItem.isVisible = true;
      this.selected.push(selectedItem);
      this.onSelect.next(item);

      if (shouldUpdateFilteredItems) {
        this.filteredAvailableItems = this.getFilteredItems(this.available);
        this.filteredSelectedItems = this.getFilteredItems(this.selected);
      }
    }
  }

  addAllAvailableGroups() {
    this.searchKeywordAvailable = '';
    this.available.forEach(availableItem => this.addSelected(availableItem, false));
    this.filteredAvailableItems = this.getFilteredItems(this.available);
    this.filteredSelectedItems = this.getFilteredItems(this.selected);
    this.initAvailableItems(true);
  }

  emptyInputField(isSelectedGroupsInput: boolean) {
    if (isSelectedGroupsInput) {
      this.selectedGroupsInput.value = '';
      this.selectedGroupsInput.inputField.nativeElement.focus();
    } else {
      this.availableGroupsInput.value = '';
      this.availableGroupsInput.inputField.nativeElement.focus();
    }
  }
}
